import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Download, Users, GraduationCap, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const { t } = useTranslation();
  const [trainees, setTrainees] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [selectedTrainee, setSelectedTrainee] = useState('');
  const [selectedInstructor, setSelectedInstructor] = useState('');
  const [loadingTrainee, setLoadingTrainee] = useState(false);
  const [loadingInstructor, setLoadingInstructor] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch trainees
      const traineesRes = await axios.get(`${API}/teacher/students`, { headers });
      setTrainees(traineesRes.data.students || []);

      // Fetch instructors (teachers)
      const instructorsRes = await axios.get(`${API}/reports/instructors`, { headers });
      setInstructors(instructorsRes.data.instructors || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadTraineeReport = async () => {
    if (!selectedTrainee) {
      toast.error(t('selectTrainee') || 'يرجى اختيار متدرب');
      return;
    }

    setLoadingTrainee(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/trainee/${selectedTrainee}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const trainee = trainees.find(t => t.id === selectedTrainee);
      link.download = `trainee_report_${trainee?.name || selectedTrainee}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      
      toast.success(t('reportDownloaded') || 'تم تحميل التقرير بنجاح');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoadingTrainee(false);
    }
  };

  const downloadInstructorReport = async () => {
    if (!selectedInstructor) {
      toast.error(t('selectInstructor') || 'يرجى اختيار مدرب');
      return;
    }

    setLoadingInstructor(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/instructor/${selectedInstructor}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const instructor = instructors.find(i => i.id === selectedInstructor);
      link.download = `instructor_report_${instructor?.name || selectedInstructor}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      
      toast.success(t('reportDownloaded') || 'تم تحميل التقرير بنجاح');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoadingInstructor(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">{t('reports') || 'التقارير'}</h2>
        <p className="text-gray-600 mt-1">{t('reportsDescription') || 'إنشاء تقارير PDF للمتدربين والمدربين'}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Trainee Reports */}
        <Card className="shadow-lg border-0">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              {t('traineeReports') || 'تقارير المتدربين'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm text-gray-600">
              {t('traineeReportDesc') || 'اختر متدرب لإنشاء تقرير PDF يتضمن نتائج الاختبارات ونسب الحضور والمشاركة'}
            </p>
            
            <Select value={selectedTrainee} onValueChange={setSelectedTrainee}>
              <SelectTrigger data-testid="trainee-select">
                <SelectValue placeholder={t('selectTrainee') || 'اختر متدرب'} />
              </SelectTrigger>
              <SelectContent>
                {trainees.map((trainee) => (
                  <SelectItem key={trainee.id} value={trainee.id}>
                    {trainee.name} ({trainee.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              data-testid="download-trainee-report"
              onClick={downloadTraineeReport}
              disabled={!selectedTrainee || loadingTrainee}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
            >
              {loadingTrainee ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {t('downloadReport') || 'تحميل التقرير'}
            </Button>
          </CardContent>
        </Card>

        {/* Instructor Reports */}
        <Card className="shadow-lg border-0">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-green-600" />
              {t('instructorReports') || 'تقارير المدربين'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm text-gray-600">
              {t('instructorReportDesc') || 'اختر مدرب لإنشاء تقرير PDF يتضمن الدورات والإحصائيات'}
            </p>
            
            <Select value={selectedInstructor} onValueChange={setSelectedInstructor}>
              <SelectTrigger data-testid="instructor-select">
                <SelectValue placeholder={t('selectInstructor') || 'اختر مدرب'} />
              </SelectTrigger>
              <SelectContent>
                {instructors.map((instructor) => (
                  <SelectItem key={instructor.id} value={instructor.id}>
                    {instructor.name} ({instructor.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              data-testid="download-instructor-report"
              onClick={downloadInstructorReport}
              disabled={!selectedInstructor || loadingInstructor}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
            >
              {loadingInstructor ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {t('downloadReport') || 'تحميل التقرير'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
